import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faBookOpen } from "@fortawesome/free-solid-svg-icons";
import { useModal, useSermon, useColumn } from "../../util/hook";

interface SlideData {
  id: number;
  category: string;
  title: string;
  description: string;
  buttonText: string;
  buttonIcon: typeof faPlay;
  backgroundImage: string;
}

const MainSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { showAlert } = useModal();
  const { sermons, loading: sermonLoading } = useSermon();
  const { columns, loading: columnLoading } = useColumn();
  const navigate = useNavigate();

  const isLoading = sermonLoading || columnLoading;

  const currentSermon = sermons[0];
  const currentColumn = columns[0];

  const slides: SlideData[] = [
    ...(currentSermon
      ? [
          {
            id: 1,
            category: "SUNDAY SERMON",
            title: currentSermon.title,
            description: `${currentSermon.scripture.join(" ")} | ${currentSermon.preacher}`,
            buttonText: "설교 듣기",
            buttonIcon: faPlay,
            backgroundImage:
              "url('https://images.unsplash.com/photo-1478147427282-58a87a120781?auto=format&fit=crop&w=1920&q=80')",
          },
        ]
      : []),
    ...(currentColumn
      ? [
          {
            id: 2,
            category: "PASTOR'S COLUMN",
            title: currentColumn.title,
            description: `${new Date(currentColumn.date.replace(/\./g, "-")).getFullYear()}년 목회 서신`,
            buttonText: "칼럼 읽기",
            buttonIcon: faBookOpen,
            backgroundImage:
              "url('https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1920&q=80')",
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (slides.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [slides.length]);

  if (isLoading) {
    return (
      <SliderContainer className="main-slider loading">
        <Slide
          className="main-slide active"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1478147427282-58a87a120781?auto=format&fit=crop&w=1920&q=80')",
          }}
        >
          <SlideContent className="slide-content">
            <SkeletonPulse
              style={{
                width: "120px",
                height: "28px",
                marginBottom: "20px",
                display: "inline-block",
              }}
            />
            <SkeletonPulse
              style={{
                width: "80%",
                height: "58px",
                margin: "0 auto 20px",
              }}
            />
            <SkeletonPulse
              style={{
                width: "60%",
                height: "22px",
                margin: "0 auto 40px",
              }}
            />
            <SkeletonPulse
              style={{
                width: "150px",
                height: "50px",
                margin: "0 auto",
              }}
            />
          </SlideContent>
        </Slide>
      </SliderContainer>
    );
  }

  if (slides.length === 0) return null;

  const handleSlideChange = (nextSlide: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(nextSlide);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const handleButtonClick = async (buttonText: string) => {
    if (buttonText === "설교 듣기") {
      // 가장 최신 설교로 이동
      if (currentSermon) {
        navigate(`/sermon/${currentSermon.id}`, { state: { backTo: "/" } });
      } else {
        await showAlert("등록된 설교가 없습니다.");
      }
    } else if (buttonText === "칼럼 읽기") {
      // 가장 최신 칼럼으로 이동
      if (currentColumn) {
        navigate(`/column/${currentColumn.id}`, { state: { backTo: "/" } });
      } else {
        await showAlert("등록된 칼럼이 없습니다.");
      }
    }
  };

  return (
    <SliderContainer className="main-slider">
      {slides.map((slide, index) => (
        <Slide
          key={slide.id}
          className={`main-slide ${currentSlide === index ? "active" : ""}`}
          style={{ backgroundImage: slide.backgroundImage }}
        >
          <SlideContent className="slide-content">
            <SlideCate className="slide-cate">{slide.category}</SlideCate>
            <SlideTitle
              className="slide-title"
              dangerouslySetInnerHTML={{ __html: slide.title }}
            />
            <SlideDesc className="slide-desc">{slide.description}</SlideDesc>
            <SlideButton
              className="btn-slide"
              onClick={() => handleButtonClick(slide.buttonText)}
            >
              <FontAwesomeIcon icon={slide.buttonIcon} /> {slide.buttonText}
            </SlideButton>
          </SlideContent>
        </Slide>
      ))}

      <DotsContainer className="main-dots">
        {slides.map((_, index) => (
          <Dot
            key={index}
            className={`main-dot ${currentSlide === index ? "active" : ""}`}
            onClick={() => handleSlideChange(index)}
          />
        ))}
      </DotsContainer>
    </SliderContainer>
  );
};

const SliderContainer = styled.section`
  position: relative;
  width: 100%;
  height: 550px;
  overflow: hidden;
  background: #000;
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.6s ease-in-out;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
  }

  &.active {
    opacity: 1;
    z-index: 2;
  }
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;

  @media (max-width: 768px) {
    max-width: 90%;
  }
`;

const SlideCate = styled.span`
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.6);
  padding: 5px 15px;
  font-size: 14px;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 300;
`;

const SlideTitle = styled.h2`
  font-size: 48px;
  margin-bottom: 20px;
  line-height: 1.2;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const SlideDesc = styled.p`
  font-size: 18px;
  font-weight: 300;
  opacity: 0.9;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const SlideButton = styled.button`
  padding: 15px 40px;
  background: transparent;
  border: 2px solid white;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: 0.3s;
  font-weight: 700;

  &:hover {
    background: #004094;
    border-color: #004094;
  }

  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 13px;
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  cursor: pointer;
  transition: 0.3s;

  &.active {
    background: white;
  }
`;

const SkeletonPulse = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

export default MainSlider;
